import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type PropsWithPartialAnchor, useExternalAnchor} from '@github-ui/react-core/react-partial-anchor'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  AgentIcon,
  BookIcon,
  CodeIcon,
  CodespacesIcon,
  type Icon,
  IssueOpenedIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonAddIcon,
  PlusIcon,
  ProjectIcon,
  RepoIcon,
  RepoPushIcon,
  SparkleIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Spinner} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {type ComponentProps, createContext, lazy, Suspense, use, useCallback, useId, useRef, useState} from 'react'

import type {CreateIssueDialogProps} from './CreateIssueDialog'
import styles from './GlobalCreateMenu.module.css'

const LazyCreateIssueDialog = lazy(() => import('./CreateIssueDialog'))

/**
 * Optionally tags create-menu analytics events with the surface they were triggered from (e.g.
 * `user_menu` when the menu is rendered inside the responsive user menu instead of the nav bar).
 */
const CreateMenuAnalyticsLocationContext = createContext<string | undefined>(undefined)

interface GlobalCreateMenuBaseProps {
  showCreateRepo?: boolean
  showImportRepo?: boolean
  showCreateOrg?: boolean
  showCodespaces?: boolean
  showSpark?: boolean
  showCodingAgent?: boolean
  showDocs?: boolean
  showGist?: boolean
  showCreateIssue?: boolean
  org?: {
    login: string
    addWord: string
  } | null
  owner?: string
  repo?: string
  side?: ComponentProps<typeof ActionMenu.Overlay>['side']
}

interface LegacyProjectProps {
  showCreateLegacyProject?: true
  showCreateProject?: false
  createProjectUrl?: string
}

interface ProjectProps {
  showCreateLegacyProject?: false
  showCreateProject: true
  createProjectUrl: string
}

export type GlobalCreateMenuProps = GlobalCreateMenuBaseProps & (LegacyProjectProps | ProjectProps)

type InternalGlobalCreateMenuProps = GlobalCreateMenuProps & {environment?: CreateIssueDialogProps['environment']}

type GlobalCreateMenuOverlayProps = InternalGlobalCreateMenuProps & {
  setIsOpen: (isOpen: boolean) => void
  isOpen: boolean
  anchorRef: React.RefObject<HTMLElement | null>
}

interface CreateMenuItemProps {
  label: string
  LeadingVisual: Icon
  analyticsKey: string
  itemRef?: React.RefObject<HTMLAnchorElement | null> | null
}

function analyticsPayload({analyticsKey, location}: Pick<CreateMenuItemProps, 'analyticsKey'> & {location?: string}) {
  return {
    category: 'GLOBAL_NAV_BAR:CREATE_MENU',
    action: analyticsKey,
    label: location ? `ref_loc:${location}` : undefined,
  }
}

function CreateMenuLinkItem({
  label,
  href,
  LeadingVisual,
  analyticsKey,
  itemRef = null,
}: CreateMenuItemProps & {
  href: string
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const location = use(CreateMenuAnalyticsLocationContext)

  const handleClick = useCallback(() => {
    sendClickAnalyticsEvent(analyticsPayload({analyticsKey, location}))
  }, [analyticsKey, location, sendClickAnalyticsEvent])

  return (
    <ActionList.LinkItem href={href} onClick={handleClick} ref={itemRef}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.LinkItem>
  )
}

function CreateMenuItem({
  label,
  onClick,
  LeadingVisual,
  analyticsKey,
}: CreateMenuItemProps & {
  onClick: () => void
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const location = use(CreateMenuAnalyticsLocationContext)

  const handleClick = useCallback(() => {
    sendClickAnalyticsEvent(analyticsPayload({analyticsKey, location}))
    onClick()
  }, [analyticsKey, location, sendClickAnalyticsEvent, onClick])

  return (
    <ActionList.Item onSelect={handleClick}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.Item>
  )
}

function MenuItemSpinner() {
  return <Spinner size="small" />
}

function GlobalCreateMenuOverlay({
  side = 'outside-bottom',
  showCreateRepo,
  showImportRepo,
  showCreateOrg,
  showCreateProject,
  createProjectUrl,
  showCreateLegacyProject,
  showCreateIssue,
  showCodespaces,
  showSpark,
  showCodingAgent,
  showDocs,
  showGist,
  org,
  owner,
  repo,
  isOpen,
  setIsOpen,
  environment,
  anchorRef,
}: GlobalCreateMenuOverlayProps) {
  const [isCreateIssueVisible, setIsCreateIssueVisible] = useState(false)
  // Has the `LazyCreateIssueDialog` loaded? We use this to know when to enable the "New issue" menu item
  const [isCreateIssueLoaded, setIsCreateIssueLoaded] = useState(false)
  // Defer lazy loading the CreateIssueDialog until the first time the menu is opened. This is a
  // one-way latch: once opened, the dialog stays mounted so it doesn't re-load on subsequent opens.
  // Set during render (guarded) rather than in an effect to avoid the extra post-open commit.
  const [createIssueIntent, setCreateIssueIntent] = useState(isOpen)
  if (isOpen && !createIssueIntent) {
    setCreateIssueIntent(true)
  }
  // this allows us to fallback to a spinner on the "New issue" menu item while the dialog is lazy-loading in,
  // but *only* after the user has clicked
  const shouldShowCreateIssueFallback = isCreateIssueVisible && !isCreateIssueLoaded
  const listRef = useRef<HTMLAnchorElement | null>(null)
  return (
    <>
      {showCreateIssue && createIssueIntent && (
        <Suspense>
          <LazyCreateIssueDialog
            isVisible={isCreateIssueVisible}
            setIsVisible={setIsCreateIssueVisible}
            setIsLoaded={setIsCreateIssueLoaded}
            setIsParentMenuOpen={setIsOpen}
            owner={owner}
            repo={repo}
            environment={environment}
            returnFocusRef={anchorRef ?? listRef}
          />
        </Suspense>
      )}
      <ActionMenu.Overlay side={side}>
        <ActionList>
          {showCreateIssue && (
            <CreateMenuItem
              label="New issue"
              analyticsKey="NEW_ISSUE"
              onClick={() => {
                setIsCreateIssueVisible(true)
                return false
              }}
              itemRef={listRef}
              LeadingVisual={shouldShowCreateIssueFallback ? MenuItemSpinner : IssueOpenedIcon}
            />
          )}
          {showCreateRepo !== false && (
            <CreateMenuLinkItem
              label="New repository"
              analyticsKey="NEW_REPOSITORY"
              href="/new"
              LeadingVisual={RepoIcon}
            />
          )}
          {showImportRepo && (
            <CreateMenuLinkItem
              label="Import repository"
              analyticsKey="IMPORT_REPOSITORY"
              href="/new/import"
              LeadingVisual={RepoPushIcon}
            />
          )}

          {(showCodingAgent || showSpark || showCodespaces || showDocs || showGist) && <ActionList.Divider />}

          {showCodingAgent && (
            <CreateMenuLinkItem
              label="New agent session"
              analyticsKey="NEW_AGENT_TASK"
              href={isFeatureEnabled('one_copilot_chat_redirect') ? '/copilot' : '/copilot/agents'}
              LeadingVisual={AgentIcon}
            />
          )}

          {showSpark && (
            <CreateMenuLinkItem label="New spark" analyticsKey="NEW_SPARK" href="/spark" LeadingVisual={SparkleIcon} />
          )}

          {(showCodespaces || showDocs || showGist) && (
            <>
              {showCodespaces && (
                <CreateMenuLinkItem
                  label="New codespace"
                  analyticsKey="NEW_CODESPACE"
                  href="/codespaces/new"
                  LeadingVisual={CodespacesIcon}
                />
              )}
              {showDocs && (
                <CreateMenuLinkItem label="New doc" analyticsKey="NEW_DOC" href="/new/doc" LeadingVisual={BookIcon} />
              )}
              {showGist && (
                <CreateMenuLinkItem label="New gist" analyticsKey="NEW_GIST" href="/gist" LeadingVisual={CodeIcon} />
              )}
            </>
          )}

          {(showCreateOrg || showCreateProject || showCreateLegacyProject) && (
            <>
              <ActionList.Divider />
              {showCreateOrg && (
                <CreateMenuLinkItem
                  label="New organization"
                  analyticsKey="NEW_ORGANIZATION"
                  href="/account/organizations/new"
                  LeadingVisual={OrganizationIcon}
                />
              )}
              {showCreateProject && (
                <CreateMenuLinkItem
                  label="New project"
                  analyticsKey="NEW_PROJECT"
                  href={createProjectUrl}
                  LeadingVisual={ProjectIcon}
                />
              )}
              {!showCreateProject && showCreateLegacyProject && (
                <CreateMenuLinkItem
                  label="New project"
                  analyticsKey="NEW_LEGACY_PROJECT"
                  href="/new/project"
                  LeadingVisual={ProjectIcon}
                />
              )}
            </>
          )}

          {org && (
            <>
              <ActionList.Divider />
              <CreateMenuLinkItem
                label={`${org.addWord} someone to ${org.login}`}
                analyticsKey="INVITE_SOMEONE"
                href={`/orgs/${org.login}/people#invite-member`}
                LeadingVisual={PersonAddIcon}
              />
              <CreateMenuLinkItem
                label={`New team in ${org.login}`}
                analyticsKey="NEW_TEAM"
                href={`/orgs/${org.login}/new-team`}
                LeadingVisual={PeopleIcon}
              />
              <CreateMenuLinkItem
                label={`New repository in ${org.login}`}
                analyticsKey="NEW_ORG_REPOSITORY"
                href={`/organizations/${org.login}/repositories/new`}
                LeadingVisual={RepoIcon}
              />
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </>
  )
}

export function ReactPartialGlobalCreateMenu({
  reactPartialAnchor,
  ...props
}: PropsWithPartialAnchor<GlobalCreateMenuProps>) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(reactPartialAnchor)

  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
      <GlobalCreateMenuOverlay {...props} isOpen={open} setIsOpen={setOpen} anchorRef={anchorRef} />
    </ActionMenu>
  )
}

export function GlobalCreateMenu({
  isLoading,
  isOpen,
  setIsOpen,
  anchorRef,
  analyticsLocation,
  className,
  hideTrailingAction,
  ...props
}: GlobalCreateMenuOverlayProps & {
  isLoading?: boolean
  analyticsLocation?: string
  className?: string
  hideTrailingAction?: boolean
}) {
  const toolTipId = `global-create-menu-tooltip-${useId()}`
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <CreateMenuAnalyticsLocationContext value={analyticsLocation}>
      <ActionMenu open={isOpen} onOpenChange={setIsOpen} anchorRef={anchorRef}>
        <Tooltip text="Create new..." type="label" id={toolTipId}>
          <ActionMenu.Button
            ref={anchorRef as React.RefObject<HTMLButtonElement>}
            variant="invisible"
            className={[
              styles.actionMenuButton,
              styles.responsiveCreateMenu,
              hideTrailingAction && styles.hideTrailingAction,
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            leadingVisual={isLoading ? MenuItemSpinner : PlusIcon}
            onClick={() =>
              sendClickAnalyticsEvent(
                analyticsPayload({
                  analyticsKey: 'CREATE_MENU_TOGGLE',
                  location: analyticsLocation,
                }),
              )
            }
          />
        </Tooltip>
        <GlobalCreateMenuOverlay {...props} isOpen={isOpen} setIsOpen={setIsOpen} anchorRef={anchorRef} />
      </ActionMenu>
    </CreateMenuAnalyticsLocationContext>
  )
}
